import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDeals } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { fmtMoney, fmtDate } from '../lib/format';
import { ArrowLeft, Plus, Trash2, Save, Mail, Phone, Link as LinkIcon, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import BrandPitchTalentsList from '../components/BrandPitchTalentsList';
import { useBrandPitches } from '../context/BrandPitchesContext';
import { Stars } from '../components/PitchRow';

const emptyContact = () => ({ id: 'c_' + Math.random().toString(36).slice(2, 10), name: '', email: '', role: '', phone: '', notes: '' });

const BrandDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { brands, brandMap, deals, updateDeal, updateBrand, removeBrand, talentMap, stages } = useDeals();
  const { items: brandPitchItems } = useBrandPitches();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);
  const brand = brandMap[id] || brands.find((b) => b.id === id);
  const stageMap = useMemo(() => Object.fromEntries(stages.map((s) => [s.id, s])), [stages]);
  const pitchRating = useMemo(
    () => Math.max(0, ...(brandPitchItems || []).filter((bp) => bp.brand_id === id).map((bp) => bp.stars || 0), 0),
    [brandPitchItems, id]
  );

  const [form, setForm] = useState({ name: '', logo: '', notes: '', billing_profiles: [], contacts: [] });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dealFilter, setDealFilter] = useState('all'); // contact filter

  useEffect(() => {
    if (brand) {
      // Start from stored profiles; if none but a legacy `billing_details` string exists, seed a default profile
      let profiles = brand.billing_profiles || [];
      if (profiles.length === 0 && (brand.billing_details || brand.vat_number || brand.invoicing_terms)) {
        profiles = [{
          id: 'bp_' + Math.random().toString(36).slice(2, 10),
          label: 'Default',
          billing_details: brand.billing_details || '',
          invoicing_terms: brand.invoicing_terms || '',
          vat_number: brand.vat_number || '',
        }];
      }
      setForm({
        name: brand.name || '', logo: brand.logo || '', notes: brand.notes || '',
        billing_profiles: profiles,
        contacts: brand.contacts || [],
      });
    }
  }, [brand?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!brand) return (
    <div className="p-8">
      <Link to="/brands" className="text-sm text-slate-500 hover:text-slate-800">← back</Link>
      <p className="mt-4 text-slate-500">brand not found.</p>
    </div>
  );

  const setField = (k, v) => { setForm((p) => ({ ...p, [k]: v })); setDirty(true); };
  const setContact = (idx, patch) => { setForm((p) => ({ ...p, contacts: p.contacts.map((c, i) => (i === idx ? { ...c, ...patch } : c)) })); setDirty(true); };
  const addContact = () => { setForm((p) => ({ ...p, contacts: [...p.contacts, emptyContact()] })); setDirty(true); };
  const removeContact = (idx) => { setForm((p) => ({ ...p, contacts: p.contacts.filter((_, i) => i !== idx) })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await updateBrand(id, {
        name: form.name.trim() || brand.name,
        logo: form.logo,
        notes: form.notes,
        billing_profiles: form.billing_profiles,
        // clear legacy fields once migrated to profiles
        billing_details: '', invoicing_terms: '', vat_number: '',
        contacts: form.contacts,
      });
      setDirty(false);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const brandDeals = deals.filter((d) => d.brand_id === brand.id);
  const visibleDeals = brandDeals.filter((d) => dealFilter === 'all' || d.contact_id === dealFilter || (dealFilter === '__none__' && !d.contact_id));
  const totals = {
    pipeline: brandDeals.filter((d) => !['paid_out'].includes(d.stage)).reduce((s, d) => s + (d.value || 0), 0),
    paid: brandDeals.filter((d) => d.stage === 'paid_out').reduce((s, d) => s + (d.value || 0), 0),
  };

  const contactMap = Object.fromEntries((form.contacts || []).map((c) => [c.id, c]));

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <button onClick={() => nav('/brands')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
            <ArrowLeft className="w-4 h-4" /> brands
          </button>
          <span className="text-slate-300">/</span>
          <h1 className="text-lg font-semibold text-slate-800">{brand.name}</h1>
        </div>
        {isManager && (
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => { if (window.confirm(`Delete ${brand.name}?`)) { removeBrand(id); nav('/brands'); } }} className="text-red-600 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-1.5" /> delete
            </Button>
            <Button disabled={!dirty || saving} onClick={save} className="bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? 'saving…' : 'save'}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <aside className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-mint-50 text-2xl flex items-center justify-center">{form.logo || brand.name.charAt(0)}</div>
            <div className="flex-1 min-w-0">
              {isManager ? (
                <>
                  <Input value={form.name} onChange={(e) => setField('name', e.target.value)} className="h-9 text-base font-semibold" />
                  <Input value={form.logo} onChange={(e) => setField('logo', e.target.value)} placeholder="emoji" className="h-8 text-sm mt-2 w-24" maxLength={4} />
                </>
              ) : (
                <h2 className="text-lg font-semibold text-slate-900 truncate">{brand.name}</h2>
              )}
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2" data-testid="brand-pitch-rating-detail">
            <span className="text-[10px] uppercase tracking-widest text-slate-400">pitch rating</span>
            {pitchRating > 0 ? <Stars value={pitchRating} testId="brand-detail-pitch-rating-stars" /> : <span className="text-xs text-slate-300">not rated yet</span>}
          </div>

          <div className="mt-5">
            <Label>Notes</Label>
            {isManager ? (
              <Textarea value={form.notes} onChange={(e) => setField('notes', e.target.value)} rows={5} className="mt-1" placeholder="payment terms, rate history, preferred contact channel…" />
            ) : (
              <p className="mt-1 text-sm text-slate-600 whitespace-pre-line">{form.notes || 'no notes yet.'}</p>
            )}
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between mb-1">
              <div>
                <Label>Billing profiles</Label>
                <p className="text-xs text-slate-500 mt-0.5">add one per payer — e.g. bill the brand direct or their agency. picked on the invoice.</p>
              </div>
              {isManager && (
                <button
                  type="button"
                  onClick={() => {
                    setForm((p) => ({ ...p, billing_profiles: [...(p.billing_profiles || []), { id: 'bp_' + Math.random().toString(36).slice(2, 10), label: '', billing_details: '', invoicing_terms: '', vat_number: '' }] }));
                    setDirty(true);
                  }}
                  className="text-xs text-pink-600 hover:text-pink-700 hover:bg-pink-50 rounded px-2 py-1"
                  data-testid="add-billing-profile-btn"
                >+ add profile</button>
              )}
            </div>
            {(form.billing_profiles || []).length === 0 && (
              <div className="text-xs text-slate-400 italic py-2">no billing profiles yet.</div>
            )}
            <div className="space-y-3">
              {(form.billing_profiles || []).map((bp, idx) => (
                <div key={bp.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50" data-testid={`billing-profile-${idx}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={bp.label}
                      onChange={(e) => {
                        setForm((p) => ({ ...p, billing_profiles: p.billing_profiles.map((x, i) => i === idx ? { ...x, label: e.target.value } : x) }));
                        setDirty(true);
                      }}
                      placeholder="Label (e.g. Direct, Via WPP Agency)"
                      disabled={!isManager}
                      className="flex-1 bg-white text-sm px-2 py-1 border border-slate-200 rounded font-medium"
                      data-testid={`billing-profile-label-${idx}`}
                    />
                    {isManager && (
                      <button
                        type="button"
                        onClick={() => {
                          setForm((p) => ({ ...p, billing_profiles: p.billing_profiles.filter((_, i) => i !== idx) }));
                          setDirty(true);
                        }}
                        className="text-xs text-slate-400 hover:text-red-600 hover:bg-red-50 rounded px-2 py-1"
                        title="remove profile"
                      >remove</button>
                    )}
                  </div>
                  <div>
                    <Label className="text-[11px]">Billing address block</Label>
                    <Textarea
                      value={bp.billing_details}
                      onChange={(e) => {
                        setForm((p) => ({ ...p, billing_profiles: p.billing_profiles.map((x, i) => i === idx ? { ...x, billing_details: e.target.value } : x) }));
                        setDirty(true);
                      }}
                      rows={4}
                      disabled={!isManager}
                      placeholder={"legal company name\naddress line 1\naddress line 2\nPO reference"}
                      className="mt-1 text-sm font-mono bg-white"
                      data-testid={`billing-profile-details-${idx}`}
                    />
                  </div>
                  <div className="space-y-2 mt-2">
                    <div>
                      <Label className="text-[11px]">Company number <span className="text-slate-400 font-normal">(optional)</span></Label>
                      <input
                        type="text"
                        value={bp.company_number || ''}
                        onChange={(e) => {
                          setForm((p) => ({ ...p, billing_profiles: p.billing_profiles.map((x, i) => i === idx ? { ...x, company_number: e.target.value } : x) }));
                          setDirty(true);
                        }}
                        disabled={!isManager}
                        placeholder="12345678"
                        className="mt-1 w-full bg-white text-sm px-2 py-1.5 border border-slate-200 rounded font-mono"
                        data-testid={`billing-profile-company-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[11px]">VAT number <span className="text-slate-400 font-normal">(optional)</span></Label>
                      <input
                        type="text"
                        value={bp.vat_number}
                        onChange={(e) => {
                          setForm((p) => ({ ...p, billing_profiles: p.billing_profiles.map((x, i) => i === idx ? { ...x, vat_number: e.target.value } : x) }));
                          setDirty(true);
                        }}
                        disabled={!isManager}
                        placeholder="GB123456789"
                        className="mt-1 w-full bg-white text-sm px-2 py-1.5 border border-slate-200 rounded font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px]">Invoicing terms</Label>
                      <input
                        type="text"
                        value={bp.invoicing_terms}
                        onChange={(e) => {
                          setForm((p) => ({ ...p, billing_profiles: p.billing_profiles.map((x, i) => i === idx ? { ...x, invoicing_terms: e.target.value } : x) }));
                          setDirty(true);
                        }}
                        disabled={!isManager}
                        placeholder="e.g. 30 days from invoice date"
                        className="mt-1 w-full bg-white text-sm px-2 py-1.5 border border-slate-200 rounded"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-lg p-3"><div className="text-[10px] uppercase tracking-wide text-slate-400">pipeline</div><div className="text-lg font-semibold text-slate-800">{fmtMoney(totals.pipeline)}</div></div>
            <div className="bg-slate-50 rounded-lg p-3"><div className="text-[10px] uppercase tracking-wide text-slate-400">paid</div><div className="text-lg font-semibold text-mint-700">{fmtMoney(totals.paid)}</div></div>
          </div>
        </aside>

        <div className="lg:col-span-2 space-y-5">
          <BrandPitchTalentsList brandId={brand.id} />

          <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">contacts</h3>
              {isManager && (
                <Button variant="ghost" size="sm" onClick={addContact} className="text-mint-700 hover:bg-mint-50">
                  <Plus className="w-4 h-4 mr-1" /> add contact
                </Button>
              )}
            </div>
            {form.contacts.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-slate-400">no contacts yet. add someone to keep in touch with.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {form.contacts.map((c, idx) => (
                  <div key={c.id} className="px-5 py-4">
                    {!isManager ? (
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800">{c.name || 'unnamed'}</span>
                          {c.role && <span className="text-xs text-slate-500">• {c.role}</span>}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                          {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>}
                          {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                        </div>
                        {c.notes && <p className="mt-1 text-xs text-slate-500 whitespace-pre-line">{c.notes}</p>}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-start">
                        <Input value={c.name} onChange={(e) => setContact(idx, { name: e.target.value })} placeholder="Name" />
                        <Input value={c.email} onChange={(e) => setContact(idx, { email: e.target.value })} placeholder="Email" />
                        <Input value={c.role} onChange={(e) => setContact(idx, { role: e.target.value })} placeholder="Role (e.g. Marketing Lead)" />
                        <div className="flex gap-2">
                          <Input value={c.phone} onChange={(e) => setContact(idx, { phone: e.target.value })} placeholder="Phone" className="flex-1" />
                          <button onClick={() => removeContact(idx)} className="p-2 text-slate-400 hover:text-red-600" title="Remove"><X className="w-4 h-4" /></button>
                        </div>
                        <Textarea value={c.notes} onChange={(e) => setContact(idx, { notes: e.target.value })} placeholder="Notes (optional)" rows={2} className="md:col-span-4" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-slate-700">deals</h3>
              {form.contacts.length > 0 && (
                <Select value={dealFilter} onValueChange={setDealFilter}>
                  <SelectTrigger className="h-8 w-56"><SelectValue placeholder="filter by contact" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">all deals</SelectItem>
                    <SelectItem value="__none__">unassigned</SelectItem>
                    {form.contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || c.email || 'unnamed contact'}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            {visibleDeals.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-slate-400">no deals yet for this brand.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left font-medium px-4 py-2">deal</th>
                    <th className="text-left font-medium px-4 py-2">talent</th>
                    <th className="text-left font-medium px-4 py-2">contact</th>
                    <th className="text-left font-medium px-4 py-2">stage</th>
                    <th className="text-right font-medium px-4 py-2">value</th>
                    <th className="text-left font-medium px-4 py-2">due</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleDeals.map((d) => (
                    <tr key={d.id} className="border-t border-slate-100">
                      <td className="px-4 py-2 text-slate-800">{d.title}<div className="text-xs text-slate-500">{d.type}</div></td>
                      <td className="px-4 py-2 text-slate-700">{talentMap[d.talent_id]?.name || '—'}</td>
                      <td className="px-4 py-2">
                        {isManager ? (
                          <Select value={d.contact_id || '__none__'} onValueChange={(v) => updateDeal(d.id, { contact_id: v === '__none__' ? null : v })}>
                            <SelectTrigger className="h-7 w-40 text-xs"><SelectValue placeholder="link…" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">—</SelectItem>
                              {form.contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || c.email || 'unnamed'}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-xs text-slate-500">{contactMap[d.contact_id]?.name || contactMap[d.contact_id]?.email || '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-2"><span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] bg-slate-100 text-slate-700"><span className="w-1.5 h-1.5 rounded-full" style={{ background: stageMap[d.stage]?.color }} />{stageMap[d.stage]?.label}</span></td>
                      <td className="px-4 py-2 text-right font-medium text-slate-900">{fmtMoney(d.value, d.currency)}</td>
                      <td className="px-4 py-2 text-slate-600">{fmtDate(d.due_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default BrandDetail;
